// Chunk generator (GEN-1). Pure and deterministic: same seed + config yields
// identical chunks on the main thread, in the gen worker, and on the server.
// Normal worlds use the climate/terrain/surface pipeline (M2); `flat` is the
// E2E/test world. Heightmap solidity is computed internally (air/water excluded)
// so worker and main-thread output match byte-for-byte.
import { WORLD, LIGHT, STRUCTURE } from '../config';
import { Chunk } from '../engine/chunk';
import { STATE_AIR, DEFAULT_RANGE, type YRange } from '../engine/section';
import { Rng } from '../engine/rng';
import { ClimateSampler, selectBiome, type Climate } from './overworld/climate';
import { TerrainShaper } from './overworld/terrain';
import { SurfacePainter } from './overworld/surface';
import { Carver } from './overworld/carvers';
import { CaveFeatures, type CaveFeatureIds } from './overworld/caves';
import { OrePlacer } from './overworld/ores';
import { FeatureGen } from './overworld/features';
import { lightChunk } from '../engine/light';
import { stampSchematic, schematicBounds, type StateResolver } from './schematic-place';
import { planStructuresInChunk, type StructureEnv, type PlacedPiece } from './structures';
import { NetherTerrain, type NetherIds } from './nether/terrain';
import { EndTerrain, type EndIds } from './end/terrain';
import { AtlasGenerator, type AtlasIds } from '../atlas/rasterize';
import type { IntermediateCity } from '../atlas/intermediate';
import type { AtlasElevation } from '../atlas/elevation';
import { EarthGenerator, type EarthWorld } from '../earth/earthgen';
import type { Biome, PlacementRule, TerrainSpline, OreConfig, Vegetation } from '../registry/schemas';

const CARVABLE_NAMES = ['stone', 'deepslate', 'dirt', 'grass_block', 'sand', 'sandstone', 'gravel', 'andesite', 'diorite', 'granite'];

/** Resolve the block-state ids the shared Atlas rasterizer needs (ATL-3/ERT-4). Used
 *  by both the Atlas and Earth branches — the Earth generator stamps through the same
 *  modules, so it wants the identical id set. */
function buildAtlasIds(bs: Record<string, number>): AtlasIds {
  const req = (name: string): number => {
    const id = bs[name];
    if (id === undefined) throw new Error(`atlas/earth generator needs block '${name}'`);
    return id;
  };
  return {
    air: STATE_AIR, grass: req('grass_block'), dirt: req('dirt'), stone: req('stone'), bedrock: req('bedrock'),
    water: req('water'), road: req('gray_concrete'), sidewalk: req('andesite'),
    log: bs['oak_log[axis=y]'] ?? req('oak_log'), leaves: req('oak_leaves'),
    brick: req('bricks'), stoneBrick: req('stone_bricks'), glass: req('glass'),
    slab: req('oak_slab'), ladder: req('ladder'), glowstone: req('glowstone'),
    runway: req('light_gray_concrete'), runwayPaint: req('white_concrete'), taxiPaint: req('yellow_concrete'),
  };
}

/** Block state ids the flat generator places (air is implicitly 0). */
export interface GeneratorIds {
  stone: number;
  dirt: number;
  grass: number;
  log: number;
  leaves: number;
}

export interface GeneratorBiomes {
  defs: readonly Biome[];
  placement: readonly PlacementRule[];
  spline: TerrainSpline;
}

/** The slice of the block-state table structure placement needs to resolve
 *  authored/rotated block keys → state ids (serializable across the worker). */
export interface StructureStates {
  /** stateId → resolved property map. */
  props: Readonly<Record<string, string>>[];
  /** block name → its state ids in enumeration order. */
  statesOf: Record<string, number[]>;
  /** block name → default (first-enumerated) state id. */
  defaults: Record<string, number>;
}

export interface GeneratorConfig {
  seed: number | string | bigint;
  flat: boolean;
  /** Which dimension to generate (DIM-1). Defaults to overworld. */
  dimension?: 'overworld' | 'nether' | 'end';
  /** Vertical extent of the generated dimension (TAL-1); absent ⇒ default range. */
  range?: YRange;
  /** Atlas city model (ATL-1); when present the world is voxelized from it. */
  atlas?: IntermediateCity;
  /** Real elevation for an Atlas world (M30/ELV-1); absent ⇒ flat Atlas ground (63). */
  atlasElevation?: AtlasElevation;
  /** Earth world (ERT-2); present ⇒ the world is voxelized from decoded TIL tiles. */
  earth?: EarthWorld;
  ids: GeneratorIds;
  /** name → default state id, for the surface painter. */
  blockStates?: Record<string, number>;
  biomes?: GeneratorBiomes;
  ores?: readonly OreConfig[];
  vegetation?: Vegetation;
  /** stateId → lightOpacity / lightEmission (0..15), for the bulk light pass. */
  opacityByState?: Uint8Array;
  emissionByState?: Uint8Array;
  /** Block-state table for structure placement (M6.3); absent ⇒ no structures. */
  structureStates?: StructureStates;
  /** Bounded child world (DOR-3, M41): a square footprint centred on the origin.
   *  `half` = size/2 blocks; columns with |x|≥half or |z|≥half get a barrier wall
   *  (the world edge). Absent ⇒ unbounded (every world before M41) → parity. */
  bounds?: { half: number };
}

/** Flat-world surface Y (grass on top) — used by `?world=test` for E2E. */
export const FLAT_SURFACE = 64;

export class ChunkGenerator {
  private readonly flat: boolean;
  private readonly dimension: 'overworld' | 'nether' | 'end';
  /** Vertical extent of the dimension this generator builds (TAL-1). */
  private readonly range: YRange;
  private readonly ids: GeneratorIds;
  private readonly waterState: number;
  private readonly isBlocking: (s: number) => boolean;
  private readonly opacityOf: (s: number) => number;
  private readonly emissionOf: (s: number) => number;

  // Nether pipeline (undefined for the overworld). Netherrack floor top is the
  // spawn-placement surface height.
  private readonly nether?: NetherTerrain;
  private readonly netherFloorTop?: (x: number, z: number) => number;

  // End pipeline (undefined unless this generator builds the End, DIM-3).
  private readonly end?: EndTerrain;

  // Atlas pipeline (undefined unless the world was created from a city model).
  private readonly atlas?: AtlasGenerator;

  // Earth pipeline (undefined unless the world was created from TIL tiles, ERT-2).
  private readonly earth?: EarthGenerator;

  // Normal-world pipeline (undefined for flat worlds).
  private readonly climate?: ClimateSampler;
  private readonly terrain?: TerrainShaper;
  private readonly surface?: SurfacePainter;
  private readonly biomeByName?: Map<string, Biome>;
  private readonly placement?: readonly PlacementRule[];
  private readonly carver?: Carver;
  private readonly orePlacer?: OrePlacer;
  private readonly featureGen?: FeatureGen;

  // Structures (M6.3): a StateResolver over the passed table + the seed.
  private readonly seed: number | string | bigint;
  private readonly structureRes?: StateResolver;
  private readonly dirtState: number;

  // Bounded child world (DOR-3, M41): the origin-centred footprint + the barrier state.
  private readonly bounds?: { half: number };
  private readonly barrierState: number;

  constructor(cfg: GeneratorConfig) {
    this.flat = cfg.flat;
    this.dimension = cfg.dimension ?? 'overworld';
    this.range = cfg.range ?? DEFAULT_RANGE;
    this.ids = cfg.ids;
    this.seed = cfg.seed;
    this.dirtState = cfg.ids.dirt;
    this.bounds = cfg.bounds;
    this.barrierState = cfg.blockStates?.bedrock ?? cfg.ids.stone;
    this.waterState = cfg.blockStates?.water ?? -1;
    this.isBlocking = (s) => s !== STATE_AIR && s !== this.waterState && s !== (cfg.blockStates?.lava ?? -1);
    // Light lookups (§5.2). Absent tables → sensible defaults: air transparent,
    // everything else opaque, nothing self-lit (keeps flat/minimal worlds valid).
    const op = cfg.opacityByState, em = cfg.emissionByState;
    this.opacityOf = op ? (s) => op[s] ?? LIGHT.MAX : (s) => (s === STATE_AIR ? 0 : LIGHT.MAX);
    this.emissionOf = em ? (s) => em[s] ?? 0 : () => 0;

    // Structure placement resolves authored keys (with properties/rotation) against
    // the passed state table (M6.3); absent table ⇒ structures off. Set up before
    // the dimension branches so the nether set (fortress / bastion) gets it too —
    // which structure defs actually apply is decided per-dimension by the planner.
    const st = cfg.structureStates;
    if (st) {
      const statesByName = new Map(Object.entries(st.statesOf));
      this.structureRes = {
        statesOf: (n) => statesByName.get(n),
        propsOf: (id) => st.props[id] ?? {},
        defaultOf: (n) => st.defaults[n],
      };
    }

    if (cfg.earth) {
      const bs = cfg.blockStates;
      if (!bs) throw new Error('earth worldgen needs blockStates');
      this.earth = new EarthGenerator(cfg.earth, buildAtlasIds(bs), cfg.seed, cfg.opacityByState, cfg.emissionByState);
      return;
    }

    if (cfg.atlas) {
      const bs = cfg.blockStates;
      if (!bs) throw new Error('atlas worldgen needs blockStates');
      this.atlas = new AtlasGenerator(cfg.atlas, buildAtlasIds(bs), cfg.seed, cfg.opacityByState, cfg.emissionByState, cfg.atlasElevation);
      return;
    }

    if (this.dimension === 'end') {
      const bs = cfg.blockStates;
      if (!bs) throw new Error('end worldgen needs blockStates');
      const req = (name: string): number => {
        const id = bs[name];
        if (id === undefined) throw new Error(`end generator needs block '${name}'`);
        return id;
      };
      const ids: EndIds = {
        endStone: req('end_stone'), obsidian: req('obsidian'), endCrystal: bs['end_crystal'],
        chorusPlant: bs['chorus_plant'], chorusFlower: bs['chorus_flower'],
      };
      this.end = new EndTerrain(cfg.seed, ids);
      return;
    }

    if (this.dimension === 'nether') {
      const bs = cfg.blockStates;
      if (!bs) throw new Error('nether worldgen needs blockStates');
      const req = (name: string): number => {
        const id = bs[name];
        if (id === undefined) throw new Error(`nether generator needs block '${name}'`);
        return id;
      };
      const ids: NetherIds = {
        bedrock: req('bedrock'), netherrack: req('netherrack'), lava: req('lava'),
        glowstone: req('glowstone'), quartzOre: req('nether_quartz_ore'), magma: req('magma_block'),
        netherGoldOre: bs['nether_gold_ore'], ancientDebris: bs['ancient_debris'],
      };
      // The biome surface pass resolves its own materials by name; a table without
      // them (an older/minimal registry) simply generates plain wastes.
      const nether = new NetherTerrain(cfg.seed, ids, (n) => bs[n]);
      this.nether = nether;
      this.netherFloorTop = (x, z) => nether.floorTopAt(x, z);
      return;
    }

    if (!cfg.flat) {
      if (!cfg.biomes || !cfg.blockStates) throw new Error('normal worldgen needs biomes + blockStates');
      const blockStates = cfg.blockStates;
      const resolve = (name: string): number => blockStates[name] ?? STATE_AIR;
      this.climate = new ClimateSampler(cfg.seed);
      this.terrain = new TerrainShaper(cfg.seed, cfg.biomes.spline);
      this.surface = new SurfacePainter(cfg.seed, resolve);
      this.biomeByName = new Map(cfg.biomes.defs.map((b) => [b.name, b]));
      this.placement = cfg.biomes.placement;

      const maxState = Math.max(0, ...Object.values(blockStates)) + 1;
      const carvable = new Uint8Array(maxState);
      for (const n of CARVABLE_NAMES) if (blockStates[n] !== undefined) carvable[blockStates[n]!] = 1;
      // Aquifers + lush caves ride on the carve (GEN-6 extension). Each group is
      // opt-in on its blocks being present, so a minimal registry still generates
      // the plain M2 caves byte-for-byte.
      const lushNames = ['moss_block', 'moss_carpet', 'glow_lichen', 'cave_vines'] as const;
      const hasLush = lushNames.every((n) => blockStates[n] !== undefined);
      const caveIds: CaveFeatureIds = {
        ...(this.waterState >= 0 ? { water: this.waterState } : {}),
        ...(hasLush ? {
          lush: {
            moss: blockStates['moss_block']!,
            mossCarpet: blockStates['moss_carpet']!,
            glowLichen: blockStates['glow_lichen']!,
            caveVines: blockStates['cave_vines[berries=0]'] ?? blockStates['cave_vines']!,
            caveVinesBerries: blockStates['cave_vines[berries=1]'] ?? blockStates['cave_vines']!,
            // The deeper dressing — clay pools with dripleaf, rooted-dirt ceilings
            // trailing roots, spore blossoms and azalea bushes. Each is picked up
            // only if its block exists, so a registry without them still generates
            // the moss/lichen/vine cave unchanged.
            ...Object.fromEntries((
              [
                ['clay', 'clay'], ['bigDripleaf', 'big_dripleaf'], ['smallDripleaf', 'small_dripleaf'],
                ['rootedDirt', 'rooted_dirt'], ['hangingRoots', 'hanging_roots'],
                ['sporeBlossom', 'spore_blossom'], ['azalea', 'azalea'], ['floweringAzalea', 'flowering_azalea'],
              ] as const
            ).flatMap(([key, name]) => (blockStates[name] !== undefined ? [[key, blockStates[name]!]] : []))),
          },
        } : {}),
      };
      const caveFeatures = new CaveFeatures(cfg.seed, caveIds);
      this.carver = new Carver(cfg.seed, carvable, this.waterState, caveFeatures.active ? caveFeatures : undefined);
      if (cfg.ores?.length) {
        this.orePlacer = new OrePlacer(
          new Rng(cfg.seed), cfg.ores, resolve, resolve('stone'), resolve('deepslate'),
          (x, z) => this.biomeNameAt(x, z),
        );
      }
      const veg = cfg.vegetation;
      if (veg && (veg.trees.length > 0 || veg.patches.length > 0)) {
        this.featureGen = new FeatureGen({
          seed: cfg.seed,
          vegetation: veg,
          resolve,
          heightAt: (x, z) => this.terrain!.heightAt(x, z, this.climate!.sample(x, z)),
          biomeNameAt: (x, z) => selectBiome(this.climate!.sample(x, z), this.placement!),
          isBlocking: this.isBlocking,
          waterState: this.waterState,
        });
      }
    }
  }

  /** Biome name at world (x,z) — used by the structure planner (M6.3). */
  biomeNameAt(x: number, z: number): string {
    if (this.earth || this.atlas) return 'plains';
    if (this.dimension === 'end') return this.end?.biomeAt(x, z) ?? 'the_end';
    if (this.dimension === 'nether') return this.nether?.biomeAt(x, z) ?? 'nether_wastes';
    if (this.flat || !this.climate) return 'plains';
    return selectBiome(this.climate.sample(x, z), this.placement!);
  }

  /** Deterministic surface height at world (x,z). */
  terrainHeight(x: number, z: number): number {
    if (this.earth) return this.earth.terrainHeight(x, z);
    if (this.atlas) return this.atlas.terrainHeight(x, z);
    if (this.end) return this.end.surfaceY(x, z);
    if (this.dimension === 'nether') return (this.netherFloorTop?.(x, z) ?? FLAT_SURFACE) + 1;
    if (this.flat || !this.climate || !this.terrain) return FLAT_SURFACE;
    return this.terrain.heightAt(x, z, this.climate.sample(x, z));
  }

  private biomeAt(x: number, z: number): { biome: Biome; climate: Climate } {
    const climate = this.climate!.sample(x, z);
    const name = selectBiome(climate, this.placement!);
    const biome = this.biomeByName!.get(name) ?? this.biomeByName!.values().next().value!;
    return { biome, climate };
  }

  generate(cx: number, cz: number): Chunk {
    const chunk = this.generateCore(cx, cz);
    if (this.bounds) this.applyBounds(chunk, cx, cz);
    return chunk;
  }

  /** The unbounded core generation for every world type; `generate` wraps it with the
   *  optional DOR-3 bounds barrier. Unbounded worlds (`bounds` absent) skip the wrap
   *  entirely, so they stay byte-identical (parity). */
  private generateCore(cx: number, cz: number): Chunk {
    const chunk = new Chunk(cx, cz, this.range);
    const set = (lx: number, y: number, lz: number, state: number): void =>
      chunk.setBlockState(lx, y, lz, state, this.isBlocking);
    const bx = cx * WORLD.SECTION, bz = cz * WORLD.SECTION;

    if (this.earth) return this.earth.generate(cx, cz);
    if (this.atlas) return this.atlas.generate(cx, cz);

    if (this.end) {
      this.end.fill(cx, cz, set);
      this.applyStructures(chunk, cx, cz, set); // end cities (end set)
      lightChunk(chunk, this.opacityOf, this.emissionOf);
      chunk.status = 'full';
      return chunk;
    }

    if (this.nether) {
      this.nether.fill(cx, cz, (lx, y, lz) => chunk.getBlockState(lx, y, lz), set);
      this.applyStructures(chunk, cx, cz, set); // fortress / bastion (nether set)
      lightChunk(chunk, this.opacityOf, this.emissionOf);
      chunk.status = 'full';
      return chunk;
    }

    if (this.flat) {
      const surf: number = FLAT_SURFACE;
      // Fill only the default −64..64 surface band — a floating floor. A tall
      // proving-ground column (M38.4) leaves the rest open air so it builds at
      // extreme Y without a pathological full-column fill (gen + RAM stay like a
      // real surface-concentrated chunk). Default-range flat worlds are byte-
      // identical: `max` clamps to WORLD.MIN_Y = the default floor.
      const floorY = Math.max(this.range.minY, WORLD.MIN_Y);
      const { stone, dirt, grass } = this.ids;
      for (let lx = 0; lx < WORLD.SECTION; lx++) {
        for (let lz = 0; lz < WORLD.SECTION; lz++) {
          for (let y = floorY; y <= surf; y++) {
            set(lx, y, lz, y === surf ? grass : y >= surf - 2 ? dirt : stone);
          }
        }
      }
      lightChunk(chunk, this.opacityOf, this.emissionOf);
      chunk.status = 'full';
      return chunk;
    }

    for (let lx = 0; lx < WORLD.SECTION; lx++) {
      for (let lz = 0; lz < WORLD.SECTION; lz++) {
        const { biome, climate } = this.biomeAt(bx + lx, bz + lz);
        const height = this.terrain!.heightAt(bx + lx, bz + lz, climate);
        this.surface!.paint(set, bx, bz, lx, lz, height, biome);
      }
    }

    this.carver?.carve(chunk); // caves (E-6 flood-checked)
    this.orePlacer?.place(chunk); // ore veins in the remaining stone
    this.featureGen?.place(chunk); // trees + vegetation on the final surface
    this.applyStructures(chunk, cx, cz, set); // villages/pyramids/dungeons (M6.3)

    lightChunk(chunk, this.opacityOf, this.emissionOf); // LIT-1 bulk pass
    chunk.status = 'full';
    return chunk;
  }

  /** DOR-3 bounds enforcement (M41): every column outside the origin-centred footprint
   *  gets a short bedrock wall above its surface, giving a bounded child world a hard
   *  edge (barrier per type; oceans beyond the earth extent already shell over, ERT-6).
   *  Below-surface terrain is left as generated. Cheap — only the handful of edge chunks
   *  near a bounded player ever load, and the wall height is fixed, not the column. */
  private applyBounds(chunk: Chunk, cx: number, cz: number): void {
    const half = this.bounds!.half;
    const bx = cx * WORLD.SECTION, bz = cz * WORLD.SECTION;
    const WALL = 4;
    let touched = false;
    for (let lx = 0; lx < WORLD.SECTION; lx++) {
      for (let lz = 0; lz < WORLD.SECTION; lz++) {
        const wx = bx + lx, wz = bz + lz;
        if (wx >= -half && wx < half && wz >= -half && wz < half) continue; // inside the footprint
        const base = Math.max(this.range.minY, Math.min(this.range.maxY - 1, this.terrainHeight(wx, wz)));
        const ceil = Math.min(this.range.maxY, base + WALL);
        for (let y = base + 1; y <= ceil; y++) chunk.setBlockState(lx, y, lz, this.barrierState, this.isBlocking);
        touched = true;
      }
    }
    if (touched) lightChunk(chunk, this.opacityOf, this.emissionOf);
  }

  /** Stamp every structure piece intersecting this chunk (STR-3), levelling the
   *  ground under razed pieces first. Only cells inside the chunk are written. */
  private applyStructures(chunk: Chunk, cx: number, cz: number, set: (lx: number, y: number, lz: number, s: number) => void): void {
    if (!this.structureRes) return;
    const env: StructureEnv = {
      terrainHeight: (x, z) => this.terrainHeight(x, z),
      biomeName: (x, z) => this.biomeNameAt(x, z),
      dimension: this.dimension,
    };
    const bx = cx * WORLD.SECTION, bz = cz * WORLD.SECTION;
    const inChunk = (x: number, z: number): boolean => Math.floor(x / WORLD.SECTION) === cx && Math.floor(z / WORLD.SECTION) === cz;
    for (const piece of planStructuresInChunk(this.seed, cx, cz, env)) {
      if (piece.ground === 'raze') this.razeFootprint(cx, cz, piece, set);
      stampSchematic(piece.schem, [piece.ox, piece.oy, piece.oz], piece.rot, this.structureRes, {
        set: (x, y, z, s) => {
          if (y < WORLD.MIN_Y || y > WORLD.MAX_Y || !inChunk(x, z)) return;
          set(x - bx, y, z - bz, s);
        },
      });
    }
  }

  /** Level a razed piece's footprint to its ground plane within this chunk:
   *  fill up to ground−1 with dirt, clear the ground and headroom above it. */
  private razeFootprint(cx: number, cz: number, piece: PlacedPiece, set: (lx: number, y: number, lz: number, s: number) => void): void {
    const b = schematicBounds(piece.schem, [piece.ox, piece.oy, piece.oz], piece.rot);
    const groundY = piece.oy + piece.groundLayer;
    const bx = cx * WORLD.SECTION, bz = cz * WORLD.SECTION;
    const x0 = Math.max(b.minX, bx), x1 = Math.min(b.maxX, bx + WORLD.SECTION - 1);
    const z0 = Math.max(b.minZ, bz), z1 = Math.min(b.maxZ, bz + WORLD.SECTION - 1);
    for (let wz = z0; wz <= z1; wz++) {
      for (let wx = x0; wx <= x1; wx++) {
        const lx = wx - bx, lz = wz - bz;
        const feet = this.terrainHeight(wx, wz);
        for (let y = feet; y <= groundY - 1; y++) if (y >= WORLD.MIN_Y && y <= WORLD.MAX_Y) set(lx, y, lz, this.dirtState);
        const top = Math.max(groundY, feet) + STRUCTURE.RAZE_CLEAR;
        for (let y = groundY; y <= top; y++) if (y >= WORLD.MIN_Y && y <= WORLD.MAX_Y) set(lx, y, lz, STATE_AIR);
      }
    }
  }
}
